/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react'
import { Progress } from 'reactstrap'
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import moment from 'moment'
import styles from './barChart.module.scss'

// eslint-disable-next-line react/function-component-definition
export const BarChart = (props) => {
  const { usersActivity, totalUsers, colorConfiguration, index, maxIndex } = props // Index represents the current week within the date picker selection (0, 1, 2, 3, 4)
  const weekdays = ['sunday', 'saturday', 'friday', 'thursday', 'wednesday', 'tuesday', 'monday'] // Reverse order due to component being flipped over y axis

  const [userActivityBarData, setUserActivityBarData] = useState()

  // Configures local data state:
  /* userActivityBarData = {
   *    monday: {
   *       users: 1,
   *       date: '2021-02-24',
   *       color: '<theme color>',
   *       height: '6px'
   *    }, ...
   * }
   */
  useEffect(() => {
    if (usersActivity) {
      const barData = {}

      weekdays.forEach((day) => {
        if (usersActivity[day]) {
          barData[day] = usersActivity[day] // Fill day object with users, date from prop

          const users = usersActivity[day]?.users
          barData[day].color =
            !users || users === 0
              ? colorConfiguration['empty'] // eslint-disable-line dot-notation
              : colorConfiguration['success'] // eslint-disable-line dot-notation

          if (users === 0 || users === 1 || !users) {
            barData[day].height = '6px'
          } else if (users < totalUsers / 3) {
            barData[day].height = '12px'
          } else if (users >= totalUsers / 3 && users < (totalUsers * 2) / 3) {
            barData[day].height = '18px'
          } else {
            barData[day].height = '24px'
          }
        } else {
          barData[day] = null
        }
      })

      setUserActivityBarData(barData)
    }
  }, [usersActivity]) // eslint-disable-line react-hooks/exhaustive-deps

  const renderTooltip = (dayOfWeek, usersActive) => (
    <Tooltip className="activityTooltip">
      <Row style={{ textAlign: 'left' }}>
        <Col>{usersActive?.date ? moment(usersActive?.date).format('dddd') : 'No Date Available'}</Col>
      </Row>
      <Row style={{ textAlign: 'left' }}>
        <Col>{usersActive?.date ? moment(usersActive?.date).format('MMMM D, YYYY') : ''}</Col>
      </Row>
      <br />
      <Row>
        <Col xs={2} style={{ marginTop: '2rem' }}>
          <div className={styles['bar-container']}>
            <div
              style={{
                backgroundColor: usersActive?.color,
                width: usersActive?.height,
              }}
              className={styles.bar}
            >
              <Progress style={{ height: usersActive?.height }} value={100} />
            </div>
          </div>
        </Col>
        <Col xs={10}>
          <Row style={{ textAlign: 'left' }}>
            <Col>{usersActive?.users}</Col>
          </Row>
          <Row style={{ textAlign: 'left' }}>
            <Col>
              <p>Users Active</p>
            </Col>
          </Row>
        </Col>
      </Row>
    </Tooltip>
  )

  // Calculates the width of the line in the footer
  // eslint-disable-next-line consistent-return
  const calculateLineWidth = () => {
    const nonNullDays = Object.values(userActivityBarData).filter((chartObject) => chartObject !== null).length

    /* eslint-disable no-else-return */
    // First chart has special characteristics as it can have 1-6 null days that need to be moved. Need to calculate line width depending on null days caused by date picker
    if (index === 0) {
      // We only need to worry about days being null in the beginning of the userActivityBarData
      if (nonNullDays <= 3) {
        // Friday, Saturday, Sunday -> We do not want any line in this case
        return '0px'
      } else if (nonNullDays === 4) {
        return '1px'
      } else if (nonNullDays === 5) {
        return '8.5px'
      } else if (nonNullDays > 5) {
        return '17px'
      }
    } else if (index === maxIndex) {
      // Last week in the date picker selection, the null days will be at the end of the week rather than the start like index 0
      if (nonNullDays === 1) {
        return '0px'
      } else if (nonNullDays === 2) {
        return '2px'
      } else if (nonNullDays === 3) {
        return '9px'
      } else {
        return '17px'
      }
    } else {
      return '17px'
    }
    /* eslint-enable no-else-return */
  }

  return (
    userActivityBarData && (
      <>
        {/* Vertical Bars */}
        <div className={styles['bar-container']}>
          {/* eslint-disable-next-line no-shadow */}
          {weekdays.map((day, index) => {
            // Skip null days
            if (userActivityBarData[day] === null) {
              return null
            }

            return (
              // eslint-disable-next-line react/no-array-index-key
              <Row key={`bar-chart-${index}`}>
                <Col>
                  <OverlayTrigger
                    className="activityTooltip"
                    trigger={['hover', 'focus']}
                    rootClose
                    placement="top"
                    overlay={renderTooltip(day, userActivityBarData[day])}
                  >
                    <div
                      style={{
                        backgroundColor: userActivityBarData[day]?.color,
                        width: userActivityBarData[day]?.height,
                      }}
                      className={styles.bar}
                    >
                      <Row>
                        <Col>
                          <Progress
                            style={{
                              height: userActivityBarData[day]?.height,
                            }}
                            value={100}
                          />
                        </Col>
                      </Row>
                    </div>
                  </OverlayTrigger>
                </Col>
              </Row>
            )
          })}
        </div>

        {/* Footer */}
        {/* eslint-disable dot-notation */}
        <Row>
          <Col>
            <div className={styles['dateRange']}>
              {userActivityBarData['monday'] !== null && <div>M</div>}

              <div
                className={calculateLineWidth() !== '0px' ? styles['line'] : styles['line-widthless']}
                style={{ width: calculateLineWidth() }}
              />

              {userActivityBarData['friday'] !== null && <div>F</div>}
            </div>
          </Col>
        </Row>
        {/* eslint-enable dot-notation */}
      </>
    )
  )
}
